import React, { useMemo, useState, useRef, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import io from "socket.io-client";

// Shared reusable components
import ResizableHeader from "../../shared/ResizableHeader";
import useInfiniteTable from "../../shared/useInfiniteTable";
import TableStatusBar from "../../shared/TableStatusBar";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import GridDesign from "../../GridDesign/GridDesign";
import SherComboBox from "../../ComboBoxes/SherComboBox";

// Context and Assets
import { useDbContext } from "../../../context/DbContext";
import { AuthContext } from "../../../context/AuthContext";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import toExcelIcon from "../../../assets/images/ToExcel.png";
import toPdfIcon from "../../../assets/images/pdf.webp";

// Default column definitions for Event Details Tracker
const getDefaultColumns = () => [
  { key: "Date", columnName: "Date", header: "Date", width: 105, align: "center", orderNo: 1, show: true },
  { key: "Time", columnName: "Time", header: "Time", width: 95, align: "center", orderNo: 2, show: true },
  { key: "EventType", columnName: "EventType", header: "Event Type", width: 110, align: "center", orderNo: 3, show: true },
  { key: "FormName", columnName: "FormName", header: "Form / Module", width: 160, align: "left", orderNo: 4, show: true },
  { key: "EntryNo", columnName: "EntryNo", header: "Entry No", width: 90, align: "center", orderNo: 5, show: true },
  { key: "Description", columnName: "Description", header: "Description", width: 330, align: "left", orderNo: 6, show: true },
  { key: "User", columnName: "User", header: "User", width: 130, align: "left", orderNo: 7, show: true },
  { key: "ComputerName", columnName: "ComputerName", header: "Workstation", width: 130, align: "left", orderNo: 8, show: true },
  { key: "EditedDate", columnName: "EditedDate", header: "Edited Date", width: 105, align: "center", orderNo: 9, show: false },
  { key: "Auto", columnName: "Auto", header: "Event ID", width: 85, align: "center", orderNo: 10, show: false },
];

function EventDetailsReport({ onClose, eventReportData, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const { user: authUser } = useContext(AuthContext) || {};

  const getCurrentDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to extract initial values from incoming eventReportData prop
  const parseInitialUser = (userProp) => {
    if (!userProp) return null;
    if (typeof userProp === "object" && userProp.label) return userProp;
    if (Array.isArray(userProp)) return { value: userProp[0], label: userProp[1] || String(userProp[0]) };
    return { value: userProp, label: String(userProp) };
  };

  const parseInitialForm = (formProp) => {
    if (!formProp) return null;
    if (typeof formProp === "object" && formProp.label) return formProp;
    if (Array.isArray(formProp)) return { value: formProp[0], label: formProp[1] || String(formProp[0]) };
    return { value: formProp, label: String(formProp) };
  };

  const parseInitialActions = (actionProp) => {
    if (!actionProp) return ["1", "2", "3"];
    if (Array.isArray(actionProp)) return actionProp.map(String);
    if (typeof actionProp === "string") {
      const parts = actionProp.split(",").map((s) => s.trim()).filter(Boolean);
      return parts.length > 0 ? parts : ["1", "2", "3"];
    }
    return ["1", "2", "3"];
  };

  // Filter States
  const [filters, setFilters] = useState(() => ({
    fromDate: eventReportData?.fromDate || getCurrentDate(),
    toDate: eventReportData?.toDate || getCurrentDate(),
    user: parseInitialUser(eventReportData?.user),
    form: parseInitialForm(eventReportData?.form),
    selectedActions: parseInitialActions(eventReportData?.action),
  }));

  // Options for Dropdowns
  const [allUsersList, setAllUsersList] = useState([]);
  const [allFormsList, setAllFormsList] = useState([]);

  // UI States
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuickAction, setSelectedQuickAction] = useState("ALL"); // ALL | Save | Edit | Delete
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);
  const [inspectEvent, setInspectEvent] = useState(null); // Selected event for Detail modal
  const [copiedTooltip, setCopiedTooltip] = useState(false);

  const formName = "Event Details Tracker";
  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns());

  // Refs for keyboard and controls
  const userInputRef = useRef(null);
  const formInputRef = useRef(null);

  // Initialize/Load Grid Column Design
  useEffect(() => {
    const cached = localStorage.getItem(`grid_design_${formName}`);
    if (cached) {
      try {
        setGridColumns(JSON.parse(cached));
        return;
      } catch (e) {
        console.error("Error parsing cached grid settings:", e);
      }
    }

    const loadDbGrid = async () => {
      if (!dbCode) return;
      try {
        const response = await axios.get(`${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`);
        const dbCols = response.data.recordset || response.data || [];
        if (dbCols.length > 0) {
          const defaults = getDefaultColumns();
          const merged = defaults.map((initCol) => {
            const dbMatch = dbCols.find(
              (c) => String(c.ColName).toLowerCase() === String(initCol.columnName).toLowerCase()
            );
            if (dbMatch) {
              return {
                ...initCol,
                width: dbMatch.Width !== null ? dbMatch.Width : initCol.width,
                orderNo: dbMatch.InNo !== null ? dbMatch.InNo : initCol.orderNo,
                header: dbMatch.Header || initCol.header,
                show: dbMatch.Vis === 1,
              };
            }
            return initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${formName}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else {
          setGridColumns(getDefaultColumns());
        }
      } catch (err) {
        setGridColumns(getDefaultColumns());
      }
    };

    loadDbGrid();
  }, [dbCode, apiBaseUrl]);

  // Load Dropdown Options (Users & Forms)
  useEffect(() => {
    const fetchDropdowns = async () => {
      if (!dbCode) return;
      try {
        // Users
        const usersRes = await axios.get(`${apiBaseUrl}/common/selectallusers/${dbCode}`);
        const userOptions = (usersRes.data || []).map((u) => ({
          value: u.agentCode || u.Auto || u.UserID || u.auto,
          label: u.userName || u.Name || `User #${u.agentCode}`,
        }));
        setAllUsersList(userOptions);

        // Forms
        const formsRes = await axios.get(`${apiBaseUrl}/common/selectAllformDatas/${dbCode}`);
        const formOptions = (formsRes.data || []).map((f) => ({
          value: f.fmrAbbreviation || f.fmrEntryNo,
          label: f.fmrAbbreviation || `Form ${f.fmrEntryNo}`,
        }));
        setAllFormsList(formOptions);
      } catch (err) {
        console.error("Error loading dropdown data:", err);
      }
    };

    fetchDropdowns();
  }, [apiBaseUrl, dbCode]);

  // Fetch Event Details from Server
  const fetchData = useCallback(async () => {
    if (!dbCode) return;
    setIsLoading(true);
    try {
      const params = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        action: filters.selectedActions.join(","),
      };

      if (filters.user && filters.user.value) {
        params.user = filters.user.value;
      }
      if (filters.form && filters.form.label) {
        params.form = filters.form.label;
      }

      const response = await axios.get(`${apiBaseUrl}/common/eventdetailsReport/${dbCode}`, {
        params,
      });

      const rawData = response.data || [];
      // Normalize row structure and ensure EventType is human-friendly
      const normalized = rawData.map((row) => {
        let typeStr = row.EventType;
        if (typeStr === 1 || typeStr === "1") typeStr = "Save";
        else if (typeStr === 2 || typeStr === "2") typeStr = "Edit";
        else if (typeStr === 3 || typeStr === "3") typeStr = "Delete";
        return {
          ...row,
          EventType: typeStr || "Other",
        };
      });

      setTableData(normalized);
      setSelectedRowIndex(null);
    } catch (error) {
      console.error("Error fetching event details report:", error);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, dbCode, filters]);

  // Trigger initial fetch when component mounts or eventReportData changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time Socket Listener for background activity updates
  useEffect(() => {
    const socket = io(apiBaseUrl);
    socket.on("dataUpdated", () => {
      // Optional subtle refresh on external changes
    });
    return () => {
      socket.disconnect();
    };
  }, [apiBaseUrl]);

  // Handle Action Checkbox changes in Filter Panel
  const handleActionToggle = (val, isChecked) => {
    if (val === "ALL") {
      setFilters((prev) => ({
        ...prev,
        selectedActions: isChecked ? ["1", "2", "3"] : [],
      }));
    } else {
      setFilters((prev) => {
        const next = isChecked
          ? [...prev.selectedActions, val]
          : prev.selectedActions.filter((a) => a !== val);
        return { ...prev, selectedActions: next };
      });
    }
  };

  const isAllActionsChecked =
    filters.selectedActions.length === 3 &&
    filters.selectedActions.includes("1") &&
    filters.selectedActions.includes("2") &&
    filters.selectedActions.includes("3");

  const handleResetFilters = () => {
    const today = getCurrentDate();
    setFilters({
      fromDate: today,
      toDate: today,
      user: null,
      form: null,
      selectedActions: ["1", "2", "3"],
    });
    setSelectedQuickAction("ALL");
    setSearchQuery("");
  };

  // Visible Columns
  const visibleColumns = useMemo(
    () => gridColumns.filter((col) => col.show),
    [gridColumns]
  );

  const gridTemplateColumns = useMemo(() => {
    return `60px ` + visibleColumns.map((col) => `${col.width}px`).join(" ");
  }, [visibleColumns]);

  // Column Resize Handler
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Filtered Rows (Quick Action + Global Client Search)
  const filteredRows = useMemo(() => {
    let rows = tableData;

    // Quick Action filter (All, Save, Edit, Delete)
    if (selectedQuickAction !== "ALL") {
      rows = rows.filter(
        (r) => String(r.EventType || "").toLowerCase() === selectedQuickAction.toLowerCase()
      );
    }

    // Quick text search query across all visible columns
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      rows = rows.filter((row) =>
        visibleColumns.some((col) => {
          const val = String(row[col.key] ?? "");
          return val.toLowerCase().includes(query);
        })
      );
    }

    return rows;
  }, [tableData, selectedQuickAction, searchQuery, visibleColumns]);

  // KPI Metrics calculation
  const metrics = useMemo(() => {
    let saves = 0;
    let edits = 0;
    let deletes = 0;
    tableData.forEach((r) => {
      const t = String(r.EventType || "").toLowerCase();
      if (t === "save") saves++;
      else if (t === "edit") edits++;
      else if (t === "delete") deletes++;
    });
    return {
      total: tableData.length,
      saves,
      edits,
      deletes,
    };
  }, [tableData]);

  // Infinite Table hook
  const {
    displayedRows,
    visibleCount,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleKeyDown: tableKeyDown,
    hasMore,
    loadAll,
    loadMore,
  } = useInfiniteTable({ items: filteredRows, initialCount: 100, step: 100 });

  // Keyboard navigation & enter to inspect
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && selectedIndex !== null && filteredRows[selectedIndex]) {
      setInspectEvent(filteredRows[selectedIndex]);
    } else {
      tableKeyDown(e);
    }
  };

  const { exportingText, runExport } = useReportExport();

  // Export to Excel
  const exportToExcel = () => {
    if (filteredRows.length === 0) {
      alert("No event records available to export!");
      return;
    }
    runExport("Generating Excel Audit Spreadsheet...", () => {
      // Map data to clean user-friendly column headers
      const exportData = filteredRows.map((row, idx) => {
        const item = { "Sl No": idx + 1 };
        visibleColumns.forEach((col) => {
          item[col.header] = row[col.key] ?? "";
        });
        return item;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EventTracker");

      // Auto-fit column widths
      const wscols = Object.keys(exportData[0] || {}).map((k) => ({
        wch: Math.max(k.length + 3, 14),
      }));
      worksheet["!cols"] = wscols;

      XLSX.writeFile(workbook, `EventDetails_Audit_${getCurrentDate()}.xlsx`);
    });
  };

  // Export to PDF
  const exportToPDF = () => {
    if (filteredRows.length === 0) {
      alert("No event records available to export!");
      return;
    }
    runExport("Generating Audit PDF Report...", () => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Title & Header Information
      doc.setFontSize(14);
      doc.setTextColor(30, 115, 190);
      doc.text("EVENT DETAILS & AUDIT TRACKER REPORT", 14, 13);

      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated: ${new Date().toLocaleString()} | Total Records: ${filteredRows.length} | Period: ${filters.fromDate} to ${filters.toDate}`,
        14,
        18
      );

      const headers = visibleColumns.map((col) => col.header);
      const rows = filteredRows.map((row, idx) => [
        idx + 1,
        ...visibleColumns.map((col) => String(row[col.key] ?? "")),
      ]);

      doc.autoTable({
        head: [["Sl No", ...headers]],
        body: rows,
        startY: 22,
        margin: { top: 22, right: 8, bottom: 8, left: 8 },
        styles: {
          fontSize: 7.5,
          cellPadding: 1.8,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [30, 115, 190],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [244, 248, 253],
        },
      });

      doc.save(`EventDetails_Audit_${getCurrentDate()}.pdf`);
    });
  };

  // Helper for Event Type Pill styling
  const renderEventTypeBadge = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "save") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Save
        </span>
      );
    }
    if (t === "edit") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Edit
        </span>
      );
    }
    if (t === "delete") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
          Delete
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
        {type || "-"}
      </span>
    );
  };

  // Inspection Drawer navigation (Previous / Next event)
  const currentInspectIdx = useMemo(() => {
    if (!inspectEvent) return -1;
    return filteredRows.findIndex((r) => r === inspectEvent);
  }, [inspectEvent, filteredRows]);

  const handleNextInspect = () => {
    if (currentInspectIdx >= 0 && currentInspectIdx < filteredRows.length - 1) {
      setInspectEvent(filteredRows[currentInspectIdx + 1]);
    }
  };

  const handlePrevInspect = () => {
    if (currentInspectIdx > 0) {
      setInspectEvent(filteredRows[currentInspectIdx - 1]);
    }
  };

  const handleCopyDescription = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedTooltip(true);
    setTimeout(() => setCopiedTooltip(false), 2000);
  };

  const inputClass =
    "w-full px-2 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  return (
    <div
      className="bg-[#d2e4fc] w-full h-full rounded-none shadow-2xl flex flex-col items-center absolute left-0 top-0 p-[1%] font-Inter select-none"
      style={{ zIndex }}
    >
      {/* Main Container - Split View Gap */}
      <div className="w-full flex-1 flex gap-3 overflow-hidden relative">
        {/* Left Side: Filter Panel (Separate White Rounded Card) */}
        <div
          className={`bg-white border border-[#b8cce0] rounded-[10px] shadow-sm h-full transition-all duration-300 flex flex-col shrink-0 overflow-y-auto ${
            isSidebarExpanded ? "w-[300px] px-4 py-4" : "w-0 overflow-hidden border-none shadow-none"
          }`}
        >
          {isSidebarExpanded && (
            <div className="flex flex-col gap-3 font-Inter">
              {/* Header with Title */}
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <img src={headeLogo} alt="Logo" className="h-5 w-auto object-contain" />
                <span className="font-bold text-slate-800 text-sm font-Inter">Event Tracker Filters</span>
              </div>

              {/* DATE RANGE SECTION */}
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">
                  DATE RANGE
                </span>

                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-700 font-bold font-Inter">From Date:</label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                    className="w-full h-[28px] px-2 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-700 font-bold font-Inter">To Date:</label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                    className="w-full h-[28px] px-2 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white"
                  />
                </div>
              </div>

              {/* AUDIT PARAMETERS SECTION */}
              <div className="flex flex-col gap-2 pt-1 border-t border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">
                  AUDIT CRITERIA
                </span>

                {/* User Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-700 font-bold font-Inter">User:</label>
                  <SherComboBox
                    comboRef={userInputRef}
                    options={allUsersList}
                    value={filters.user?.label || ""}
                    placeholder="All Users"
                    onSelect={(opt) => setFilters({ ...filters, user: opt })}
                    onClear={() => setFilters({ ...filters, user: null })}
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Form / Module Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Form / Module:</label>
                  <SherComboBox
                    comboRef={formInputRef}
                    options={allFormsList}
                    value={filters.form?.label || ""}
                    placeholder="All Forms"
                    onSelect={(opt) => setFilters({ ...filters, form: opt })}
                    onClear={() => setFilters({ ...filters, form: null })}
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Actions Multi-select */}
                <div className="flex flex-col gap-1.5 mt-1">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Action Types:</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-2 flex flex-col gap-1.5 text-xs text-slate-700 font-medium">
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-blue-900">
                      <input
                        type="checkbox"
                        checked={isAllActionsChecked}
                        onChange={(e) => handleActionToggle("ALL", e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span>Select All Actions</span>
                    </label>
                    <div className="h-px bg-slate-200 my-0.5"></div>
                    <label className="flex items-center gap-2 cursor-pointer text-emerald-800">
                      <input
                        type="checkbox"
                        value="1"
                        checked={filters.selectedActions.includes("1")}
                        onChange={(e) => handleActionToggle("1", e.target.checked)}
                        className="rounded text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Save (Create)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-amber-800">
                      <input
                        type="checkbox"
                        value="2"
                        checked={filters.selectedActions.includes("2")}
                        onChange={(e) => handleActionToggle("2", e.target.checked)}
                        className="rounded text-amber-600 focus:ring-amber-500"
                      />
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span> Edit (Update)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-rose-800">
                      <input
                        type="checkbox"
                        value="3"
                        checked={filters.selectedActions.includes("3")}
                        onChange={(e) => handleActionToggle("3", e.target.checked)}
                        className="rounded text-rose-600 focus:ring-rose-500"
                      />
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-rose-500"></span> Delete (Remove)
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex flex-col gap-2 mt-auto pt-3 border-t border-gray-200">
                <button
                  onClick={fetchData}
                  disabled={isLoading}
                  className="w-full bg-[#1e73be] hover:bg-blue-700 text-white font-bold py-2 rounded shadow-sm text-xs font-Inter transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isLoading ? "LOADING..." : "VIEW REPORT"}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={exportToExcel}
                    className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-1.5 rounded shadow-sm text-xs font-Inter cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <img className="h-3.5 w-auto" src={toExcelIcon} alt="Excel" />
                    EXCEL
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-1.5 rounded shadow-sm text-xs font-Inter cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <img className="h-3.5 w-auto" src={toPdfIcon} alt="PDF" />
                    PDF
                  </button>
                </div>

                <button
                  onClick={handleResetFilters}
                  className="w-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 font-semibold py-1 rounded text-[11px] font-Inter transition cursor-pointer"
                >
                  Reset All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Vertical Tab Toggle Button */}
        <div
          onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          title={isSidebarExpanded ? "Collapse Filters" : "Expand Filters"}
          className={`w-[20px] h-[40px] bg-[#1e73be] hover:bg-blue-700 text-white flex items-center justify-center cursor-pointer select-none font-bold shadow-md absolute top-1/2 -translate-y-1/2 z-20 rounded-r transition-all duration-300 ${
            isSidebarExpanded ? "left-[300px]" : "left-0"
          }`}
        >
          {isSidebarExpanded ? "«" : "»"}
        </div>

        {/* Right Side: Report Grid & Table (Separate White Rounded Card) */}
        <div className="flex-1 h-full bg-white border border-[#b8cce0] rounded-[10px] shadow-sm flex flex-col p-4 overflow-hidden">
          {/* Header Row on top of report */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-3 font-Inter">
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-800 text-lg">Event Details Tracker</span>
              <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-0.5 rounded-full text-[11px] font-semibold">
                Total Records: {filteredRows.length}
              </span>
            </div>

            {/* Quick KPI Stats & Filter Pills */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setSelectedQuickAction("ALL")}
                className={`px-2.5 py-1 rounded text-[11px] font-bold border transition cursor-pointer flex items-center gap-1.5 ${
                  selectedQuickAction === "ALL"
                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                    : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                }`}
              >
                <span>All</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedQuickAction === "ALL" ? "bg-blue-700 text-white" : "bg-white text-slate-700"}`}>
                  {metrics.total}
                </span>
              </button>

              <button
                onClick={() => setSelectedQuickAction("Save")}
                className={`px-2.5 py-1 rounded text-[11px] font-bold border transition cursor-pointer flex items-center gap-1.5 ${
                  selectedQuickAction === "Save"
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                    : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                <span>Save</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedQuickAction === "Save" ? "bg-emerald-700 text-white" : "bg-white text-emerald-800"}`}>
                  {metrics.saves}
                </span>
              </button>

              <button
                onClick={() => setSelectedQuickAction("Edit")}
                className={`px-2.5 py-1 rounded text-[11px] font-bold border transition cursor-pointer flex items-center gap-1.5 ${
                  selectedQuickAction === "Edit"
                    ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                    : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                <span>Edit</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedQuickAction === "Edit" ? "bg-amber-700 text-white" : "bg-white text-amber-800"}`}>
                  {metrics.edits}
                </span>
              </button>

              <button
                onClick={() => setSelectedQuickAction("Delete")}
                className={`px-2.5 py-1 rounded text-[11px] font-bold border transition cursor-pointer flex items-center gap-1.5 ${
                  selectedQuickAction === "Delete"
                    ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                    : "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                <span>Delete</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedQuickAction === "Delete" ? "bg-rose-700 text-white" : "bg-white text-rose-800"}`}>
                  {metrics.deletes}
                </span>
              </button>
            </div>

            {/* Actions: Excel, PDF, Refresh, Grid Design, Close */}
            <div className="flex items-center gap-2">
              <button
                onClick={exportToExcel}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
                title="Export to Excel"
              >
                <img className="h-3.5 w-auto" src={toExcelIcon} alt="Excel" />
                Excel
              </button>

              <button
                onClick={exportToPDF}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
                title="Export to PDF"
              >
                <img className="h-3.5 w-auto" src={toPdfIcon} alt="PDF" />
                PDF
              </button>

              <button
                onClick={fetchData}
                disabled={isLoading}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
                title="Refresh Records"
              >
                <svg
                  className={`w-3.5 h-3.5 text-slate-500 ${isLoading ? "animate-spin text-blue-600" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3m0 0l3 3m-3-3v12" />
                </svg>
                Refresh
              </button>

              <button
                onClick={() => setShowGridDesignModal(true)}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center"
                title="Design Grid Columns"
              >
                ⋮
              </button>

              <button
                onClick={onClose}
                className="bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center ml-1"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Search bar & quick info */}
          <div className="w-full flex justify-between items-center pb-3">
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="font-bold text-slate-800">{displayedRows.length}</span> of{" "}
              <span className="font-bold text-slate-800">{filteredRows.length}</span> events
              {searchQuery && (
                <span className="ml-2 text-blue-600">
                  (filtered from {tableData.length} total)
                </span>
              )}
            </div>

            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400 h-[28px]"
              />
              <svg
                className="absolute left-2.5 top-2 h-3.5 w-3.5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Scrollable Data Table Container */}
          <div
            ref={tableContainerRef}
            className="w-full flex-1 min-h-0 overflow-auto border border-slate-300 rounded text-slate-800 text-[11px] font-semibold font-Inter outline-none focus:ring-1 focus:ring-blue-400"
            tabIndex={0}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
          >
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-white/70">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="font-Inter text-xs text-blue-900 font-semibold">Loading Event Audit Log...</span>
                </div>
              </div>
            ) : (
              <div className="min-w-max flex flex-col min-h-full">
                {/* Header Row utilizing CSS Grid */}
                <div className="sticky top-0 z-10 bg-[#1e73be] border-b border-slate-300 shrink-0">
                  <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                    <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center">
                      Sl No
                    </div>
                    {visibleColumns.map((col) => (
                      <ResizableHeader key={col.key} col={col} formName={formName} onResize={handleColumnResize}>
                        <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center w-full h-full truncate">
                          {col.header}
                        </div>
                      </ResizableHeader>
                    ))}
                  </div>
                </div>

                {/* Data Rows */}
                <div className="bg-white flex-1">
                  {filteredRows.length === 0 ? (
                    <div className="font-Inter text-xs text-gray-400 text-center py-16 flex flex-col items-center gap-2">
                      <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>No event records found matching current filters.</span>
                    </div>
                  ) : (
                    displayedRows.map((row, index) => {
                      const isHighlighted = index === selectedIndex || index === selectedRowIndex;
                      return (
                        <div
                          key={row.Auto || index}
                          data-row-index={index}
                          onClick={() => {
                            setSelectedRowIndex(index);
                            setSelectedIndex(index);
                          }}
                          onDoubleClick={() => setInspectEvent(row)}
                          className={`grid gap-0 h-8 border-b border-slate-300 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150 ${
                            isHighlighted
                              ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                              : index % 2 === 0
                              ? "bg-white"
                              : "bg-[#f2f7ff]"
                          }`}
                          style={{ gridTemplateColumns }}
                        >
                          {/* Sl No */}
                          <div className="font-Inter text-[11px] border-r border-slate-300 px-3 text-slate-700 flex items-center justify-center">
                            {index + 1}
                          </div>

                          {/* Dynamic Columns */}
                          {visibleColumns.map((col) => {
                            const val = row[col.key] ?? "";

                            // Special Column Renderers
                            if (col.key === "EventType") {
                              return (
                                <div key={col.key} className="border-r border-slate-300 px-2 flex items-center justify-center">
                                  {renderEventTypeBadge(val)}
                                </div>
                              );
                            }

                            if (col.key === "FormName") {
                              return (
                                <div key={col.key} className="border-r border-slate-300 px-2 flex items-center truncate text-slate-800">
                                  <span className="bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[10px] font-semibold truncate">
                                    {val || "-"}
                                  </span>
                                </div>
                              );
                            }

                            if (col.key === "EntryNo") {
                              return (
                                <div key={col.key} className="border-r border-slate-300 px-2 flex items-center justify-center font-mono font-bold text-blue-700">
                                  {val || "-"}
                                </div>
                              );
                            }

                            if (col.key === "User") {
                              return (
                                <div key={col.key} className="border-r border-slate-300 px-2 flex items-center gap-1.5 truncate text-slate-800">
                                  <svg className="w-3 h-3 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <span className="truncate">{val || "-"}</span>
                                </div>
                              );
                            }

                            if (col.key === "Description") {
                              return (
                                <div
                                  key={col.key}
                                  className="border-r border-slate-300 px-2 flex items-center truncate text-slate-700 hover:text-blue-700"
                                  title="Double-click to inspect full details"
                                >
                                  <span className="truncate">{String(val)}</span>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={col.key}
                                className={`border-r border-slate-300 px-2 flex items-center truncate ${
                                  col.align === "center"
                                    ? "justify-center"
                                    : col.align === "right"
                                    ? "justify-end text-right"
                                    : "justify-start text-left"
                                }`}
                              >
                                <span className="truncate">{String(val)}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table Bottom Status Bar */}
          <TableStatusBar
            visibleCount={visibleCount}
            totalRows={filteredRows.length}
            hasMore={hasMore}
            onLoadAll={loadAll}
            onLoadMore={loadMore}
          />
        </div>
      </div>

      {/* Grid Design Customization Modal */}
      {showGridDesignModal && (
        <GridDesign
          formName={formName}
          initialColumns={gridColumns}
          onClose={() => setShowGridDesignModal(false)}
          onSave={(newCols) => {
            setGridColumns(newCols);
            setShowGridDesignModal(false);
          }}
          zIndex={zIndex ? zIndex + 50 : 10000}
        />
      )}

      {/* Event Details Inspector Modal */}
      {inspectEvent && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4"
          style={{ zIndex: (zIndex ? zIndex + 60 : 10010) }}
        >
          <div className="bg-white w-full max-w-xl rounded-xl shadow-2xl border border-slate-200 overflow-hidden font-Inter flex flex-col">
            {/* Modal Header */}
            <div className="bg-[#1e73be] text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">Event Audit Inspector</span>
                {inspectEvent.Auto && (
                  <span className="bg-blue-800 text-blue-100 text-[10px] px-2 py-0.5 rounded-full font-mono">
                    ID #{inspectEvent.Auto}
                  </span>
                )}
              </div>
              <button
                onClick={() => setInspectEvent(null)}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded p-1 text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 flex flex-col gap-4 max-h-[75vh] overflow-y-auto">
              {/* Event Badge and Form Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-semibold">Action:</span>
                  {renderEventTypeBadge(inspectEvent.EventType)}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-semibold">Module / Form:</span>
                  <span className="bg-slate-100 text-slate-800 font-bold px-2 py-0.5 rounded text-xs">
                    {inspectEvent.FormName || "N/A"}
                  </span>
                </div>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3.5 rounded-lg border border-slate-200 text-xs">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Event Date & Time</span>
                  <span className="font-bold text-slate-800">
                    {inspectEvent.Date || "-"} at {inspectEvent.Time || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Voucher / Entry No</span>
                  <span className="font-bold text-blue-700 font-mono text-sm">
                    {inspectEvent.EntryNo || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Operator / User</span>
                  <span className="font-bold text-slate-800">
                    {inspectEvent.User || "System"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Workstation / Computer</span>
                  <span className="font-bold text-slate-800">
                    {inspectEvent.ComputerName || "Local Station"}
                  </span>
                </div>
                {inspectEvent.EditedDate && (
                  <div className="col-span-2">
                    <span className="text-slate-400 block text-[10px] uppercase font-bold">Last Edited Date</span>
                    <span className="font-semibold text-slate-700">
                      {inspectEvent.EditedDate}
                    </span>
                  </div>
                )}
              </div>

              {/* Full Description Box */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700">Complete Description / Log Details:</span>
                  <button
                    onClick={() => handleCopyDescription(inspectEvent.Description)}
                    className="text-blue-600 hover:text-blue-800 text-[11px] font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    {copiedTooltip ? (
                      <span className="text-emerald-600 font-bold">✓ Copied!</span>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Log
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-slate-900 text-slate-100 p-3.5 rounded-lg text-xs font-mono leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap select-text">
                  {inspectEvent.Description || "No additional description details available."}
                </div>
              </div>
            </div>

            {/* Modal Footer with Stepper Controls */}
            <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevInspect}
                  disabled={currentInspectIdx <= 0}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 cursor-pointer"
                >
                  ← Previous Event
                </button>
                <button
                  onClick={handleNextInspect}
                  disabled={currentInspectIdx >= filteredRows.length - 1}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 cursor-pointer"
                >
                  Next Event →
                </button>
              </div>

              <button
                onClick={() => setInspectEvent(null)}
                className="px-4 py-1.5 bg-[#1e73be] hover:bg-blue-700 text-white rounded text-xs font-bold cursor-pointer"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default EventDetailsReport;